import {
    UnitContextMenuClickParams,
    UnitResource,
    UnitStateData,
    UnitStatusSubmenuView,
} from '@mark43/cad-api';
import {
    modals,
    useToast,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuSub,
    ContextMenuSubContent,
    ContextMenuSubTrigger,
    Text,
} from '@cad-arc';
import { TransportStatusSubmenu } from './transport-status-submenu';
import { AdminStatusSubmenu } from './admin-status-submenu';
import { useCallback } from 'react';
import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { formatUnitStateData } from './utils';
import { useUpdateDispositionModal } from '../dispositions/hooks/use-update-disposition-modal';

export function UnitStatusSubmenu({
    unitStatusSubmenuView,
    clickParams,
}: {
    unitStatusSubmenuView: UnitStatusSubmenuView;
    clickParams: UnitContextMenuClickParams;
}): React.ReactElement {
    const apiClient = useApiClient();
    const drawerControls = useDrawerControls();
    const toast = useToast();
    const { dispatchConfirmUnassigningEvents, unitLevelDispositionsEnabled } = useEnabledFeatures();
    const { openEventPanelAndShowUpdateDispositionModal } = useUpdateDispositionModal({
        eventId: clickParams.eventId,
        unitId: clickParams.unitId,
        isPrimaryUnit: clickParams.isPrimary,
    });

    const setUnitStatus = useCallback(
        (unitStateData: UnitStateData) => {
            return apiClient
                .request<UnitResource.CreateUnitStatuses>({
                    method: 'POST',
                    url: 'unit/status/',
                    data: [
                        // @ts-expect-error not including all fields
                        {
                            unitId: clickParams.unitId,
                            unitStateId: unitStateData.id,
                            agencyEventId: clickParams.eventId,
                        },
                    ],
                })
                .catch(
                    createApiErrorHandler(() => {
                        toast({
                            status: 'error',
                            description: errorOccurredWhile(
                                `transitioning unit to status ${unitStateData.value}`
                            ),
                        });
                    })
                );
        },
        [apiClient, clickParams, toast]
    );
    const handleSelect = useCallback(
        async (unitStateData: UnitStateData): Promise<void> => {
            if (
                unitLevelDispositionsEnabled &&
                clickParams.isPrimary &&
                !unitStateData.isEventRelated
            ) {
                return Promise.resolve(openEventPanelAndShowUpdateDispositionModal());
            }
            if (
                dispatchConfirmUnassigningEvents &&
                clickParams.isOnlyUnitAssignedToEvent &&
                !unitStateData.isEventRelated &&
                !(await modals.confirm({
                    title: '',
                    body: (
                        <>
                            <Text>
                                Set {clickParams.callSign} to {unitStateData.value}?
                            </Text>
                            <br />
                            <Text>
                                Event {clickParams.assignedEventNumber} (
                                {clickParams.assignedEventLocation}) will become unassigned.
                            </Text>
                        </>
                    ),
                }))
            ) {
                return undefined;
            }

            switch (unitStateData.unitStateGroup) {
                case 'ADMIN':
                case 'SERVICE_ASSIGNMENT':
                    drawerControls.open({
                        type: 'status-admin',
                        props: { unitId: clickParams.unitId, statusId: unitStateData.id },
                    });
                    break;
                case 'DIRECTED_PATROL':
                    drawerControls.open({
                        type: 'status-directed-patrol',
                        props: { unitId: clickParams.unitId, statusId: unitStateData.id },
                    });
                    break;
                case 'SPECIAL_ASSIGNMENT':
                    drawerControls.open({
                        type: 'status-special-assignment',
                        props: { unitId: clickParams.unitId, statusId: unitStateData.id },
                    });
                    break;
                case 'VIRTUAL_PATROL':
                    drawerControls.open({
                        type: 'status-virtual-patrol',
                        props: { unitId: clickParams.unitId, statusId: unitStateData.id },
                    });
                    break;
                case 'TRANSPORT_EN_ROUTE':
                case 'HOSPITAL_TRANSPORT_EN_ROUTE':
                case 'HOSPITAL_TRANSPORT_ARRIVED':
                case 'TRANSPORT_ARRIVED':
                    drawerControls.open({
                        type: 'status-transport',
                        props: {
                            unitId: clickParams.unitId,
                            unitStateId: unitStateData.id,
                            agencyEventId: clickParams.eventId,
                        },
                    });
                    break;
                default:
                    setUnitStatus(unitStateData);
                    break;
            }
        },
        [
            setUnitStatus,
            drawerControls,
            dispatchConfirmUnassigningEvents,
            unitLevelDispositionsEnabled,
            clickParams,
            openEventPanelAndShowUpdateDispositionModal,
        ]
    );

    return unitStatusSubmenuView.isVisible ? (
        <ContextMenuSub>
            <ContextMenuSubTrigger data-pendo-id="menu-item-unit-status">
                {unitStatusSubmenuView.label}
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
                <>
                    {unitStatusSubmenuView.unitStatesGroup1.map((unitStateData) => {
                        return (
                            <ContextMenuItem
                                key={unitStateData.id}
                                onSelect={() => {
                                    handleSelect(unitStateData);
                                }}
                            >
                                {formatUnitStateData(unitStateData)}
                            </ContextMenuItem>
                        );
                    })}
                    <TransportStatusSubmenu
                        transportStatusSubmenuView={
                            unitStatusSubmenuView.transportStatusSubmenuView
                        }
                        onSelect={handleSelect}
                    />
                    {unitStatusSubmenuView.showSeparator1 && (
                        <ContextMenuSeparator data-testid="unit-status-submenu-separator-1" />
                    )}
                    {unitStatusSubmenuView.unitStatesGroup2.map((unitStateData) => {
                        return (
                            <ContextMenuItem
                                key={unitStateData.id}
                                onSelect={() => {
                                    handleSelect(unitStateData);
                                }}
                            >
                                {unitStateData.value}
                            </ContextMenuItem>
                        );
                    })}
                    <AdminStatusSubmenu
                        adminStatusSubmenuView={unitStatusSubmenuView.adminStatusSubmenuView}
                        onSelect={handleSelect}
                    />
                    {unitStatusSubmenuView.showSeparator2 && (
                        <ContextMenuSeparator data-testid="unit-status-submenu-separator-2" />
                    )}
                    {unitStatusSubmenuView.unitStatesGroup3.map((unitStateData) => {
                        return (
                            <ContextMenuItem
                                key={unitStateData.id}
                                onSelect={() => {
                                    handleSelect(unitStateData);
                                }}
                            >
                                {formatUnitStateData(unitStateData)}
                            </ContextMenuItem>
                        );
                    })}
                </>
            </ContextMenuSubContent>
        </ContextMenuSub>
    ) : (
        <></>
    );
}
